
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function IndustryGrid() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const industries = [
    {
      icon: 'ri-tools-line',
      title: 'Home Services & Trade',
      description: 'Automation for lead routing, dispatch optimization, and revenue consistency.',
      image: 'https://public.readdy.ai/ai/img_res/aa74bb84495c7a172adccfb17be126a2.jpg',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Healthcare & Clinical Practices',
      description: 'AI-powered intake, scheduling, and operational precision systems.',
      image: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20clinical%20practice%20interior%20with%20advanced%20digital%20technology%20screens%20and%20clean%20minimalist%20design%20dark%20moody%20atmosphere%20with%20subtle%20teal%20ambient%20lighting%20premium%20medical%20facility%20photography%20cinematic&width=600&height=400&seq=ind-health-1&orientation=landscape',
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Law Practices',
      description: 'Structured case intake, follow-up automation, and client lifecycle control.',
      image: 'https://readdy.ai/api/search-image?query=sophisticated%20modern%20law%20office%20with%20dark%20wood%20and%20digital%20screens%20showing%20data%20analytics%20moody%20dark%20lighting%20with%20subtle%20teal%20accent%20glow%20premium%20corporate%20interior%20photography%20cinematic%20style&width=600&height=400&seq=ind-law-1&orientation=landscape',
    },
    {
      icon: 'ri-bank-line',
      title: 'Financial Services & Lending',
      description: 'Qualification funnels, advisory automation, and pipeline management.',
      image: 'https://readdy.ai/api/search-image?query=modern%20financial%20services%20office%20with%20multiple%20screens%20showing%20data%20dashboards%20and%20analytics%20dark%20premium%20interior%20with%20subtle%20teal%20ambient%20lighting%20corporate%20enterprise%20photography%20cinematic%20moody&width=600&height=400&seq=ind-finance-1&orientation=landscape',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Real Estate Brokerage',
      description: 'Lead intelligence, nurture automation, and deal flow systemization.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20real%20estate%20office%20with%20modern%20technology%20displays%20showing%20property%20analytics%20dark%20sophisticated%20interior%20with%20teal%20accent%20lighting%20premium%20corporate%20photography%20cinematic%20style%20moody%20atmosphere&width=600&height=400&seq=ind-realestate-1&orientation=landscape',
    },
  ];

  return (
    <section id="industries" className="relative py-32 px-6 bg-slate-950">
      <div ref={ref} className="relative z-10 max-w-7xl mx-auto">
        {/* Section Headline */}
        <div className={`text-center mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-teal-400 text-xs font-medium tracking-widest uppercase mb-4 block">Specialized Solutions</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 tracking-tight">
            Industries We Engineer for Performance
          </h2>
          <p className="text-gray-400 text-base max-w-2xl mx-auto">
            Purpose-built AI systems designed for the unique demands of service-driven businesses.
          </p>
        </div>

        {/* Top Row - 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-16 mb-4">
          {industries.slice(0, 3).map((industry, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 1) * 100}ms` }}
            >
              {/* Image */}
              <div className="relative w-full h-72 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
              </div>

              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-teal-500/10 border border-teal-500/20">
                    <i className={`${industry.icon} text-lg text-teal-400`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-white tracking-tight">
                    {industry.title}
                  </h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {industry.description}
                </p>
                <div className="mt-4 flex items-center gap-2 text-teal-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Learn more</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row - 2 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {industries.slice(3).map((industry, index) => (
            <div
              key={index + 3}
              className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 4) * 100}ms` }}
            >
              {/* Image */}
              <div className="relative w-full h-72 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
              </div>

              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-teal-500/10 border border-teal-500/20">
                    <i className={`${industry.icon} text-lg text-teal-400`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-white tracking-tight">
                    {industry.title}
                  </h3>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {industry.description}
                </p>
                <div className="mt-4 flex items-center gap-2 text-teal-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Learn more</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
